import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Play, FloppyDisk, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SharedSet() {
    const { token } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/public/sets/${token}`).then((r) => setData(r.data)).catch(() => setError("Set not found or no longer shared."));
    }, [token]);

    const startPractice = () => {
        if (!data?.items?.length) return;
        if (!user) {
            toast.error("Log in to launch a practice session");
            nav("/login");
            return;
        }
        nav("/practice", { state: { questions: data.items, label: `Shared set — ${data.name}` } });
    };

    const saveToMySets = async () => {
        if (!user) {
            toast.error("Log in to save this set");
            nav("/login");
            return;
        }
        setSaving(true);
        try {
            await api.post("/sets", {
                name: data.name,
                question_ids: data.items.map((q) => q.id),
            });
            toast.success("Saved to your study sets");
        } catch {
            toast.error("Failed to save");
        }
        setSaving(false);
    };

    if (error) {
        return (
            <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="shared-not-found">
                <h1 className="font-display text-3xl font-semibold">{error}</h1>
                <p className="mt-4 text-muted-foreground">Ask whoever shared the link to re-generate it, or head back to the homepage.</p>
                <Button className="mt-6" onClick={() => nav("/")}>Home</Button>
            </div>
        );
    }
    if (!data) return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground">Loading shared set…</div>;

    return (
        <div className="mx-auto max-w-4xl px-6 py-12" data-testid="shared-set-page">
            <div className="mb-8 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
                <div>
                    <div className="overline text-primary flex items-center gap-2"><ShareNetwork weight="duotone" size={14} /> Shared study set</div>
                    <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">{data.name}</h1>
                    <p className="mt-2 text-muted-foreground">{data.count} questions · read-only preview</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={startPractice} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="shared-start">
                        <Play size={14} className="mr-1" /> Start practice
                    </Button>
                    <Button variant="outline" disabled={saving} onClick={saveToMySets} data-testid="shared-save">
                        <FloppyDisk size={14} className="mr-1" /> Save to my sets
                    </Button>
                </div>
            </div>

            <div className="space-y-5">
                {data.items.map((q, i) => (
                    <div key={q.id} className="border border-border bg-card p-6" data-testid={`shared-q-${i}`}>
                        <div className="overline text-primary">{q.domain}</div>
                        <p className="mt-2 text-lg leading-relaxed">{i + 1}. {q.question}</p>
                        <ul className="mt-4 space-y-2 text-sm">
                            {q.options.map((o, j) => (
                                <li key={j} className={`border p-3 ${j === q.correct_index ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border"}`}>
                                    <span className="font-mono text-xs text-primary">{String.fromCharCode(65 + j)}</span> · {o}
                                </li>
                            ))}
                        </ul>
                        {q.explanation && <p className="mt-4 border-l-2 border-primary bg-secondary/40 p-3 text-sm text-muted-foreground">{q.explanation}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}
